import { Icon } from "@/components/common/Icon";
import { Button } from "@/components/common";

import { ORG_STEPS, type OrgForm, type OrgStepId } from "./types";

interface Props {
  form: OrgForm;
  completed: Record<string, boolean>;
  onResume: (id: OrgStepId) => void;
  onExit: () => void;
}

export function OrgSuccess({ form, completed, onResume, onExit }: Props) {
  const pendingSteps = ORG_STEPS.filter((s) => !completed[s.id]);
  const allDone = pendingSteps.length === 0;

  if (allDone) {
    return (
      <div className="onb-success">
        <div className="onb-success-icon">
          <Icon name="check" size={36} stroke={2.5} />
        </div>
        <h1 className="onb-step-title is-center">Tudo pronto!</h1>
        <p className="onb-step-sub is-center">
          <strong className="onb-strong-fg1">
            {form.nomeOrg || "Sua organização"}
          </strong>{" "}
          está configurada e operando. Empresas serão criadas automaticamente a
          partir do certificado anexado.
        </p>
        <div className="onb-success-foot">
          <Button variant="primary" iconLeft="arrow-up-right" onClick={onExit}>
            Ir para o dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="onb-success">
      <div className="onb-success-icon">
        <Icon name="check" size={36} stroke={2.5} />
      </div>
      <h1 className="onb-step-title is-center">Organização criada!</h1>
      <p className="onb-step-sub is-center">
        <strong className="onb-strong-fg1">
          {form.nomeOrg || "Sua organização"}
        </strong>{" "}
        está cadastrada. Pra operar com automações você ainda precisa do
        certificado.
      </p>

      <div className="onb-success-checklist">
        <div className="onb-success-checklist-head">
          <div>
            <div className="onb-section-title onb-success-checklist-title">
              Pendências
            </div>
            <div className="onb-section-sub">
              Você pode completar agora ou depois nas configurações da
              organização.
            </div>
          </div>
          <span className="tag-chip onb-success-checklist-tag">
            {pendingSteps.length} restantes
          </span>
        </div>

        <div className="onb-success-list">
          {ORG_STEPS.filter((s) => s.id !== "org").map((s) => {
            const isCompleted = !!completed[s.id];
            return (
              <div
                key={s.id}
                className={`onb-success-item ${isCompleted ? "is-completed" : ""}`}
              >
                <span
                  className={`onb-success-bullet ${isCompleted ? "ok" : "pending"}`}
                >
                  {isCompleted ? (
                    <Icon name="check" size={13} stroke={3} />
                  ) : (
                    <Icon name={s.icon} size={13} />
                  )}
                </span>
                <div className="onb-success-item-body">
                  <div className="onb-success-item-title">{s.label}</div>
                  <div className="onb-success-item-sub">
                    {isCompleted
                      ? "Concluída"
                      : s.id === "certificado"
                        ? "Sem isso, a org não opera — empresas não serão criadas automaticamente."
                        : s.id === "contatos"
                          ? "Quem o time do TD procura quando precisa alinhar com a organização."
                          : "Opcional"}
                  </div>
                </div>
                {!isCompleted && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconRight="arrow-up-right"
                    onClick={() => onResume(s.id)}
                  >
                    {s.id === "certificado" ? "Anexar agora" : "Continuar"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="onb-success-foot">
        <Button variant="ghost" onClick={onExit}>
          Ir para o dashboard
        </Button>
        <Button
          variant="primary"
          iconRight="arrow-up-right"
          onClick={() => onResume(pendingSteps[0]?.id || "certificado")}
        >
          Continuar configurando
        </Button>
      </div>
    </div>
  );
}
